import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Player, { AutoplayProps } from '../..';
import { ScriptService } from '../../../../controllers/ScriptService';
import { AlertType, HeaderDisplayType, HeaderInteractionType, Picture, StreamType } from '../../../../interfaces';
import { getAggregatedTitleImage } from '../../../../queries/ats/common';
import { PlayData } from '../../../../queries/ats/playback/api';
import { PlayablePlaybackOption } from '../../../../queries/ats/titles/types/playback-option';
import { PlayableTitle } from '../../../../queries/ats/titles/types/title';
import { useAuth } from '../../../../utils/auth-utils';
import { showAlert } from '../../../../views/app/actions';
import Header from '../../../../views/app/Header';
import StickyHeader from '../../../../views/app/StickyHeader';
import { PlayerFullscreenPortal } from '../../../player-fullscreen-portal';
import { PlayerCard } from '../player-card';
import { isShakaError, PlayerError, ShakaError } from '../player-error';

interface ClientPlayerProps {
  data: PlayData;
  title: PlayableTitle;
  playbackOption: PlayablePlaybackOption;
  position: number | undefined;
  reportConsumption: (position: number) => void;
  stopConsumption: (position: number) => void;
  autoplay?: AutoplayProps;
}

export function ClientPlayer({
  data,
  title,
  playbackOption,
  position,
  reportConsumption,
  stopConsumption,
  autoplay,
}: ClientPlayerProps) {
  const { t } = useTranslation();
  const shouldFadeOut = useSelector((state) => state.app.shouldFadeOut);

  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useAuth();

  const [error, setError] = useState<unknown>();

  function handleOnAlert(titleString: string, message: string[]) {
    dispatch(
      showAlert({
        title: titleString,
        text: message,
        type: AlertType.INFO,
      }),
    );
  }

  const stream = {
    sessionId: data.sessionId,
    manifestUrl: data.url,
    licenseUrl: data.licenseUrl,
    customData: data.token,
    platform: data.platform,
    account: data.account,
    releasePid: data.releasePid,
    capBandwidthAt: data.capBandwidthAt,
    platformTitleId: playbackOption.platformTitleId,
    selectedPlaybackOption: playbackOption,
    title: title,
  };

  if (error instanceof PlayerError) {
    throw error;
  }

  if (error instanceof Error) {
    throw new PlayerError(t('unknown error'), error.message, {
      sessionId: data.sessionId,
      position: position,
      cause: error,
    });
  }

  if (isShakaError(error)) {
    throw new ShakaError(error.detail, {
      sessionId: data.sessionId,
      position: position,
      cause: error,
    });
  }

  if (error !== undefined) {
    throw new PlayerError(t('unknown error'), JSON.stringify(error), {
      sessionId: data.sessionId,
      position: position,
      cause: error,
    });
  }

  return (
    <div className="ats-player">
      {ScriptService.isCasting() && (
        <img className="chromecast" alt="chromecast" src={getAggregatedTitleImage(title?.masterId, 'wide', 2000)} />
      )}
      <StickyHeader overPlayer={true} />
      <Header
        displayType={HeaderDisplayType.Player}
        interactionType={HeaderInteractionType.Back}
        closeCallback={() => {
          history.goBack();
        }}
      />

      <PlayerFullscreenPortal>
        {!shouldFadeOut && <PlayerCard title={title} playbackOption={playbackOption} />}
      </PlayerFullscreenPortal>

      <Player
        casting={playbackOption.casting}
        autoplay={autoplay}
        stream={{
          startBookmark: position,
          picture: { poster: getAggregatedTitleImage(title.masterId, 'wide', 1000) } as Picture,
          isGuest: auth.isGuest,
          streamType: getHuaweiStreamType(playbackOption),
          mediaId: data.mediaId,
          contentId: playbackOption.platformTitleId,
          playData: stream,
        }}
        onConcurrencyError={(e) => setError(e)}
        onPlayerError={(e) => setError(e)}
        hideControls={shouldFadeOut}
        onAlert={handleOnAlert}
        onBookmark={(event) => {
          reportConsumption(event.position);
        }}
        onUnmount={(event) => {
          stopConsumption(event.position);
        }}
        onEnded={() => {
          history.goBack();
        }}
        currentAsset={undefined}
      />
    </div>
  );
}

function getHuaweiStreamType(playbackOption: PlayablePlaybackOption) {
  switch (playbackOption.serviceType) {
    case 'program':
      return StreamType.CATCHUP;
    case 'vod':
    case 'svod':
      return StreamType.VOD;
    default:
      return StreamType.VOD;
  }
}
